import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Button,
  Tooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Paper,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TuneIcon from "@mui/icons-material/Tune";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import api, { API_BASE_URL } from "../../src/utils/api";
import { ThemeContext } from "../../src/utils/ThemeContext";

const FILTER_KEY = "members-filter-state";

const loadFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_KEY) || "{}");
  } catch {
    return {};
  }
};

const MembersGridPage = () => {
  const theme = useTheme();
  const { isDark } = useContext(ThemeContext);
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // All filter state. initialized from sessionStorage so navigation preserves them
  const [searchTerm, setSearchTerm] = useState(() => loadFilters().searchTerm || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => loadFilters().searchTerm || "");
  const [addressFilter, setAddressFilter] = useState(() => loadFilters().addressFilter || "");
  const [membershipTypeFilter, setMembershipTypeFilter] = useState(() => loadFilters().membershipTypeFilter || "");
  const [statusFilter, setStatusFilter] = useState(() => loadFilters().statusFilter || "");
  const [sortModel, setSortModel] = useState(() => loadFilters().sortModel || []);

  // Auto-open filter panel if any field filter was persisted
  const [showFilters, setShowFilters] = useState(() => {
    const f = loadFilters();
    return !!(f.addressFilter || f.membershipTypeFilter || f.statusFilter);
  });

  const [viewMode, setViewMode] = useState(() => {
    // Persistent user preference wins; fall back to session filter state, then "table"
    return localStorage.getItem("members-view-mode") || loadFilters().viewMode || "table";
  });

  const DEFAULT_COLUMN_ORDER = [
    "CompanyCode", "ClubMemberCode", "PhotoGraphicFileName", "LastName", "FirstName",
    "ClubMembershipTypeCode", "ClubStatusRuleCode", "EmailAddress", "FullAddress",
  ];
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const userId = localStorage.getItem("userId");
      const stored = localStorage.getItem(`members-col-order-${userId}`);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Load column order from server preferences on mount
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "guest") return;
    api.get(`/user-preferences/${userId}`).then((res) => {
      const saved = res.data?.preferences?.members_column_order;
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setColumnOrder(parsed);
          localStorage.setItem(`members-col-order-${userId}`, saved);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  const [colOrderOpen, setColOrderOpen] = useState(false);
  const [draftOrder, setDraftOrder] = useState(null);
  const dragColRef = React.useRef(null);

  const openColOrderDialog = useCallback((cols) => {
    setDraftOrder(cols.map((c) => ({ field: c.field, headerName: c.headerName })));
    setColOrderOpen(true);
  }, []);

  const saveColumnOrder = useCallback((newOrder) => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "guest") return;
    const json = JSON.stringify(newOrder);
    localStorage.setItem(`members-col-order-${userId}`, json);
    // Persist to server (fire-and-forget, fetch current prefs first to avoid overwriting other fields)
    api.get(`/user-preferences/${userId}`).then((res) => {
      const prefs = res.data?.preferences || {};
      return api.put(`/user-preferences/${userId}`, { ...prefs, members_column_order: json });
    }).catch(() => {});
  }, []);
  const [cardVisibleCount, setCardVisibleCount] = useState(12);

  // Recently viewed members. persisted in sessionStorage
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("members-recently-viewed") || "[]");
    } catch {
      return [];
    }
  });

  const saveRecentlyViewed = useCallback((member) => {
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((m) => m.ClubMemberCode !== member.ClubMemberCode);
      const updated = [
        {
          ClubMemberCode: member.ClubMemberCode,
          FirstName: member.FirstName,
          LastName: member.LastName,
          PhotoGraphicFileName: member.PhotoGraphicFileName,
        },
        ...filtered,
      ].slice(0, 8);
      sessionStorage.setItem("members-recently-viewed", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearRecentlyViewed = () => {
    setRecentlyViewed([]);
    sessionStorage.removeItem("members-recently-viewed");
  };

  const limit = useMemo(() => {
    if (viewMode === "cards") return isXs ? 24 : 48;
    return 100;
  }, [viewMode, isXs]);

  // Custom fields
  const [customFieldCols, setCustomFieldCols] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});

  useEffect(() => {
    api.get("/custom-field-config/active")
      .then(res => {
        const cols = (res.data || []).filter(f => f.show_in_members !== false);
        setCustomFieldCols(cols);
      })
      .catch(() => {});
  }, []);

  // Persist filter state to sessionStorage on every change
  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({ searchTerm, addressFilter, membershipTypeFilter, statusFilter, viewMode, sortModel })
    );
  }, [searchTerm, addressFilter, membershipTypeFilter, statusFilter, viewMode, sortModel]);

  // Save view mode preference persistently so it survives navigation and mobile visits
  useEffect(() => {
    localStorage.setItem("members-view-mode", viewMode);
  }, [viewMode]);

  // On mount: purge any stale members cache entries (older than TTL or missing timestamp)
  useEffect(() => {
    const TTL = 3 * 60 * 1000;
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("members-page-"))
      .forEach((k) => {
        try {
          const { ts } = JSON.parse(sessionStorage.getItem(k)) || {};
          if (!ts || Date.now() - ts > TTL) sessionStorage.removeItem(k);
        } catch {
          sessionStorage.removeItem(k);
        }
      });
  }, []);

  // Fetch custom field values whenever the members list changes (including cache hits)
  useEffect(() => {
    if (!members.length) return;
    const codes = members.map(m => m.ClubMemberCode?.trim()).filter(Boolean);
    api.post("/members/custom-fields/batch", { memberCodes: codes })
      .then(cfRes => setCustomFieldValues(cfRes.data || {}))
      .catch(() => {});
  }, [members]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, addressFilter, membershipTypeFilter, statusFilter, limit]);

  useEffect(() => {
    setCardVisibleCount(isXs ? 12 : 24);
  }, [isXs, viewMode, currentPage, debouncedSearch]);

  useEffect(() => {
    fetchMembers(currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, limit]);

  const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

  const fetchMembers = async (page, search, { force = false } = {}) => {
    setLoading(true);

    const cacheKey = `members-page-${page}-limit-${limit}-search-${search}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);

    if (!force && cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        const age = Date.now() - (cached.ts || 0);
        if (age < CACHE_TTL_MS) {
          setMembers(cached.members || []);
          setTotalRecords(cached.totalRecords || 0);
          setLoading(false);
          return;
        }
      } catch {}
      sessionStorage.removeItem(cacheKey);
    }

    if (force) sessionStorage.removeItem(cacheKey);

    try {
      const response = await api.get("/membersfilter", {
        params: { page, limit, search },
        headers: force ? { "Cache-Control": "no-cache" } : undefined,
      });

      const data = response.data || {};
      setMembers(data.members || []);
      setTotalRecords(data.totalRecords || 0);

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ members: data.members || [], totalRecords: data.totalRecords || 0, ts: Date.now() })
      );
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrent = () => fetchMembers(currentPage, debouncedSearch, { force: true });

  const clearAllMembersCache = () => {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("members-page-"))
      .forEach((k) => sessionStorage.removeItem(k));
    fetchMembers(currentPage, debouncedSearch, { force: true });
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setAddressFilter("");
    setMembershipTypeFilter("");
    setStatusFilter("");
  };

  // Derive dropdown options from currently loaded members
  const membershipTypes = useMemo(
    () => [...new Set(members.map((m) => m.ClubMembershipTypeCode?.trim()).filter(Boolean))].sort(),
    [members]
  );

  const statusRules = useMemo(
    () => [...new Set(members.map((m) => m.ClubStatusRuleCode?.trim()).filter(Boolean))].sort(),
    [members]
  );

  // Merge custom field values directly into rows so DataGrid can sort/filter natively
  const enrichedMembers = useMemo(() => {
    if (!Object.keys(customFieldValues).length) return members;
    return members.map(m => {
      const code = m.ClubMemberCode?.trim();
      const cfVals = customFieldValues[code];
      return cfVals ? { ...m, ...cfVals } : m;
    });
  }, [members, customFieldValues]);

  const displayedRows = useMemo(() => {
    // Backend already handles search filtering (including village name via DataMart subquery).
    // Client-side only applies the extra panel filters.
    let rows = enrichedMembers;

    // Address filter
    if (addressFilter.trim()) {
      const af = addressFilter.trim().toLowerCase();
      rows = rows.filter((m) => String(m.FullAddress || "").toLowerCase().includes(af));
    }

    // Membership type filter
    if (membershipTypeFilter) {
      rows = rows.filter((m) => m.ClubMembershipTypeCode?.trim() === membershipTypeFilter);
    }

    // Status filter
    if (statusFilter) {
      rows = rows.filter((m) => m.ClubStatusRuleCode?.trim() === statusFilter);
    }

    return rows;
  }, [enrichedMembers, addressFilter, membershipTypeFilter, statusFilter]);

  const hasActiveFilters = !!(debouncedSearch || addressFilter || membershipTypeFilter || statusFilter);

  const cardsRows = useMemo(() => {
    if (viewMode !== "cards") return displayedRows;
    return displayedRows.slice(0, cardVisibleCount);
  }, [displayedRows, viewMode, cardVisibleCount]);

  const customFieldVisibility = useMemo(() => {
    const obj = {};
    customFieldCols.forEach(f => { obj[f.field_key] = true; });
    return obj;
  }, [customFieldCols]);

  const columnVisibilityModel = useMemo(() => {
    if (isXs) {
      return {
        CompanyCode: false,
        PhotoGraphicFileName: false,
        ClubMembershipTypeCode: false,
        ClubStatusRuleCode: false,
        EmailAddress: true,
        FullAddress: true,
        LastName: true,
        FirstName: true,
        ClubMemberCode: true,
        ...customFieldVisibility,
      };
    }
    if (isSmDown) {
      return {
        CompanyCode: false,
        PhotoGraphicFileName: true,
        ClubMembershipTypeCode: true,
        ClubStatusRuleCode: false,
        EmailAddress: true,
        FullAddress: true,
        LastName: true,
        FirstName: true,
        ClubMemberCode: true,
        ...customFieldVisibility,
      };
    }
    return {
      CompanyCode: true,
      PhotoGraphicFileName: true,
      ClubMembershipTypeCode: true,
      ClubStatusRuleCode: true,
      EmailAddress: true,
      FullAddress: true,
      LastName: true,
      FirstName: true,
      ClubMemberCode: true,
      ...customFieldVisibility,
    };
  }, [isXs, isSmDown, customFieldVisibility]);

  const columns = useMemo(
    () => [
      { field: "CompanyCode", headerName: "Company Code", flex: 0.6, minWidth: 90 },
      {
        field: "ClubMemberCode",
        headerName: "Member Code",
        flex: 0.8,
        minWidth: 130,
        renderCell: (params) => (
          <Button
            component={RouterLink}
            to={`/home/clubmembers/${params.row.ClubMemberCode}`}
            variant="text"
            size="small"
            onClick={() => saveRecentlyViewed(params.row)}
            sx={{
              color: "#2f63ad",
              fontWeight: 700,
              textTransform: "none",
              px: 0,
              minWidth: 0,
            }}
          >
            {params.value?.trim()}
          </Button>
        ),
      },
      {
        field: "PhotoGraphicFileName",
        headerName: "Photo",
        flex: 0.5,
        minWidth: 72,
        sortable: false,
        renderCell: (params) => (
          <img
            loading="lazy"
            src={`${API_BASE_URL}/images/${encodeURIComponent(
              params.row?.PhotoGraphicFileName?.trim() || ""
            )}`}
            alt="Member"
            onError={(e) => (e.currentTarget.src = "/img/100x100.png")}
            style={{ width: 42, height: 42, borderRadius: "999px", objectFit: "cover" }}
          />
        ),
      },
      { field: "LastName", headerName: "Last Name", flex: 1, minWidth: 120 },
      { field: "FirstName", headerName: "First Name", flex: 1, minWidth: 120 },
      { field: "ClubMembershipTypeCode", headerName: "Membership Type", flex: 0.8, minWidth: 130 },
      { field: "ClubStatusRuleCode", headerName: "Status Rule", flex: 0.7, minWidth: 110 },
      {
        field: "EmailAddress",
        headerName: "Email",
        flex: 1.2,
        minWidth: 190,
        renderCell: (params) => {
          const email = params.value?.trim();
          return email ? (
            <Tooltip title={`Send email to ${email}`}>
              <Box
                component="a"
                href={`mailto:${email}`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#2f63ad",
                  textDecoration: "none",
                  minWidth: 0,
                }}
              >
                <EmailIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {email}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            "N/A"
          );
        },
      },
      {
        field: "FullAddress",
        headerName: "Address",
        flex: 1.6,
        minWidth: 220,
        renderCell: (params) => {
          const address = params.value?.trim();
          const mapsLink = address
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
            : null;

          return address ? (
            <Tooltip title={`View ${address} on Google Maps`}>
              <Box
                component="a"
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "#2f63ad",
                  textDecoration: "none",
                  minWidth: 0,
                }}
              >
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {address}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            "N/A"
          );
        },
      },
      ...customFieldCols.map(cf => ({
        field: cf.field_key,
        headerName: cf.display_label,
        flex: 1,
        minWidth: 150,
        sortable: true,
      })),
    ],
    [saveRecentlyViewed, customFieldCols]
  );

  // Apply saved column order
  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;
    const map = Object.fromEntries(columns.map((c) => [c.field, c]));
    const ordered = columnOrder.map((f) => map[f]).filter(Boolean);
    // Append any columns not in the saved order (e.g. new custom fields)
    const inOrder = new Set(columnOrder);
    columns.forEach((c) => { if (!inOrder.has(c.field)) ordered.push(c); });
    return ordered;
  }, [columns, columnOrder]);

  const gridMinWidth = useMemo(() => {
    const minWidthMap = {
      CompanyCode: 90,
      ClubMemberCode: 130,
      PhotoGraphicFileName: 72,
      LastName: 120,
      FirstName: 120,
      ClubMembershipTypeCode: 130,
      ClubStatusRuleCode: 110,
      EmailAddress: 190,
      FullAddress: 220,
    };

    const visibleFields = Object.entries(columnVisibilityModel)
      .filter(([, visible]) => visible)
      .map(([field]) => field);

    const total = visibleFields.reduce((sum, field) => sum + (minWidthMap[field] || 140), 0);
    const cfTotal = customFieldCols.reduce((sum, cf) => sum + 150, 0);
    return Math.max(520, total + cfTotal + 40);
  }, [columnVisibilityModel, customFieldCols]);

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / limit));

  const pageCardSx = {
    borderRadius: "18px",
    border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    borderRadius: "16px",
    border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2.25,
    boxShadow: "none",
    backgroundColor: "#2f63ad",
    "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
    "&:active": { transform: "translateY(1px)" },
    "&:disabled": { backgroundColor: "#90caf9", color: "#fff" },
    whiteSpace: "nowrap",
  };

  const secondaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: isDark ? "#b8c9de" : "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
    "&:active": { transform: "translateY(1px)" },
  };

  const MemberCard = ({ m }) => {
    // eslint-disable-next-line no-shadow
    const { isDark } = useContext(ThemeContext);
    const email = m.EmailAddress?.trim();
    const address = m.FullAddress?.trim();
    const mapsLink = address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: "16px",
          border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          backgroundColor: isDark ? "#1e2e47" : "rgba(255,255,255,0.96)",
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
            transform: "translateY(-1px)",
          },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <img
              loading="lazy"
              src={`${API_BASE_URL}/images/${encodeURIComponent(
                m?.PhotoGraphicFileName?.trim() || ""
              )}`}
              alt="Member"
              onError={(e) => (e.currentTarget.src = "/img/100x100.png")}
              style={{
                width: 48,
                height: 48,
                borderRadius: "999px",
                flex: "0 0 auto",
                objectFit: "cover",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            />

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 800, color: isDark ? "#dde5f0" : "#223142" }} noWrap>
                {m.LastName?.trim() || "N/A"}, {m.FirstName?.trim() || "N/A"}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mt: 0.75 }}>
                <Chip size="small" label={m.ClubMemberCode?.trim() || "No Code"} sx={{ fontWeight: 700 }} />
                {m.CompanyCode?.trim() ? (
                  <Chip size="small" variant="outlined" label={m.CompanyCode.trim()} sx={{ fontWeight: 600 }} />
                ) : null}
                {m.ClubMembershipTypeCode?.trim() ? (
                  <Chip size="small" variant="outlined" label={m.ClubMembershipTypeCode.trim()} sx={{ fontWeight: 600 }} />
                ) : null}
              </Box>
            </Box>

            <Button
              component={RouterLink}
              to={`/home/clubmembers/${m.ClubMemberCode}`}
              variant="outlined"
              size="small"
              onClick={() => saveRecentlyViewed(m)}
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{ ...secondaryButtonSx, fontWeight: 800 }}
            >
              Open
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Stack spacing={1.1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, width: 90, color: "text.secondary", flexShrink: 0 }}
              >
                Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {m.ClubStatusRuleCode?.trim() || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, width: 90, color: "text.secondary", flexShrink: 0 }}
              >
                Email
              </Typography>

              {email ? (
                <Box
                  component="a"
                  href={`mailto:${email}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "#2f63ad",
                    textDecoration: "none",
                    minWidth: 0,
                  }}
                >
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {email}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">N/A</Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, width: 90, color: "text.secondary", flexShrink: 0 }}
              >
                Address
              </Typography>

              {address ? (
                <Box
                  component="a"
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "#2f63ad",
                    textDecoration: "none",
                    minWidth: 0,
                  }}
                >
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {address}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2">N/A</Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(47,99,173,0.08)",
            }}
          >
            <GroupIcon sx={{ color: "#2f63ad" }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142" }}>
            Club Members
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c" }}>
          Search, browse, and open member profiles in table or card view.
        </Typography>
      </Box>

      {/* Recently viewed strip */}
      {recentlyViewed.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: isDark ? "#8a9ab8" : "#6b778c", letterSpacing: "0.3px" }}
            >
              Recently Viewed
            </Typography>
            <Button
              size="small"
              onClick={clearRecentlyViewed}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#9aa6b2",
                fontSize: "0.75rem",
                minWidth: 0,
                px: 1,
                "&:hover": { color: "#d32f2f", backgroundColor: "transparent" },
              }}
            >
              Clear
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: "auto",
              pb: 0.75,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {recentlyViewed.map((m) => (
              <Box
                key={m.ClubMemberCode}
                component={RouterLink}
                to={`/home/clubmembers/${m.ClubMemberCode}`}
                sx={{ textDecoration: "none", flexShrink: 0 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.9,
                    borderRadius: "12px",
                    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(47,99,173,0.12)",
                    backgroundColor: isDark ? "#1e2e47" : "rgba(255,255,255,0.96)",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(47,99,173,0.06)",
                      borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(47,99,173,0.28)",
                      boxShadow: "0 4px 12px rgba(47,99,173,0.1)",
                    },
                  }}
                >
                  <img
                    src={`${API_BASE_URL}/images/${encodeURIComponent(
                      m.PhotoGraphicFileName?.trim() || ""
                    )}`}
                    alt="Member"
                    onError={(e) => (e.currentTarget.src = "/img/100x100.png")}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "999px",
                      objectFit: "cover",
                      flexShrink: 0,
                      border: "1px solid rgba(47,99,173,0.1)",
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? "#dde5f0" : "#223142",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m.LastName}, {m.FirstName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: isDark ? "#6b7fa0" : "#8a94a6", whiteSpace: "nowrap", display: "block" }}
                    >
                      {m.ClubMemberCode}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Toolbar card */}
      <Paper elevation={0} sx={{ ...pageCardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        {/* Row 1: global search + action buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, member code, email, or village name..."
            size="small"
            fullWidth
            sx={{ ...fieldSx, maxWidth: { xs: "100%", md: 500 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
            <Button
              variant={showFilters ? "contained" : "outlined"}
              onClick={() => setShowFilters((v) => !v)}
              startIcon={<TuneIcon />}
              sx={showFilters ? primaryButtonSx : secondaryButtonSx}
            >
              Filters
              {hasActiveFilters && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    width: 18,
                    height: 18,
                    borderRadius: "999px",
                    backgroundColor: showFilters ? "rgba(255,255,255,0.3)" : "#2f63ad",
                    color: showFilters ? "#fff" : "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {[debouncedSearch, addressFilter, membershipTypeFilter, statusFilter].filter(Boolean).length}
                </Box>
              )}
            </Button>

            <Button
              variant="contained"
              onClick={refreshCurrent}
              disabled={loading}
              startIcon={<RefreshIcon />}
              sx={primaryButtonSx}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>

            <Button
              variant="outlined"
              onClick={clearAllMembersCache}
              disabled={loading}
              startIcon={<ClearAllIcon />}
              sx={secondaryButtonSx}
            >
              Clear Cache
            </Button>

            <Button
              variant="outlined"
              onClick={() => openColOrderDialog(orderedColumns)}
              startIcon={<ViewColumnIcon />}
              sx={secondaryButtonSx}
            >
              Columns
            </Button>

            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 1.5,
                  borderColor: "rgba(47,99,173,0.18)",
                  color: "#223142",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(47,99,173,0.08)",
                    color: "#2f63ad",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <ViewListIcon fontSize="small" sx={{ mr: 0.75 }} />
                Table
              </ToggleButton>
              <ToggleButton value="cards">
                <ViewModuleIcon fontSize="small" sx={{ mr: 0.75 }} />
                Cards
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Row 2: collapsible per-field filter panel */}
        <Collapse in={showFilters}>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <TextField
              label="Address"
              value={addressFilter}
              onChange={(e) => setAddressFilter(e.target.value)}
              placeholder="e.g. Sea"
              size="small"
              sx={{ ...fieldSx, minWidth: 200, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" sx={{ color: "#6b778c" }} />
                  </InputAdornment>
                ),
                endAdornment: addressFilter ? (
                  <InputAdornment position="end">
                    <Box
                      component="span"
                      onClick={() => setAddressFilter("")}
                      sx={{ cursor: "pointer", color: "#9aa6b2", fontSize: 16, lineHeight: 1, "&:hover": { color: "#223142" } }}
                    >
                      ✕
                    </Box>
                  </InputAdornment>
                ) : null,
              }}
            />

            <FormControl size="small" sx={{ ...fieldSx, minWidth: 200, flex: 1 }}>
              <InputLabel>Membership Type</InputLabel>
              <Select
                value={membershipTypeFilter}
                label="Membership Type"
                onChange={(e) => setMembershipTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {membershipTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...fieldSx, minWidth: 200, flex: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusRules.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                sx={{ ...secondaryButtonSx, color: "#d32f2f", borderColor: "rgba(211,47,47,0.3)", "&:hover": { borderColor: "#d32f2f", color: "#d32f2f", backgroundColor: "rgba(211,47,47,0.04)" } }}
              >
                Clear All
              </Button>
            )}
          </Box>
        </Collapse>

        {/* Row 3: active filter chips */}
        {hasActiveFilters && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.75, alignItems: "center" }}>
            <Typography variant="caption" sx={{ color: "#8a94a6", fontWeight: 600, mr: 0.5 }}>
              Active filters:
            </Typography>
            {debouncedSearch && (
              <Chip
                label={`Search: "${debouncedSearch}"`}
                onDelete={() => setSearchTerm("")}
                size="small"
                sx={{ borderRadius: "999px", fontWeight: 600 }}
              />
            )}
            {addressFilter && (
              <Chip
                label={`Address: "${addressFilter}"`}
                onDelete={() => setAddressFilter("")}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ borderRadius: "999px", fontWeight: 600 }}
              />
            )}
            {membershipTypeFilter && (
              <Chip
                label={`Type: ${membershipTypeFilter}`}
                onDelete={() => setMembershipTypeFilter("")}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ borderRadius: "999px", fontWeight: 600 }}
              />
            )}
            {statusFilter && (
              <Chip
                label={`Status: ${statusFilter}`}
                onDelete={() => setStatusFilter("")}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ borderRadius: "999px", fontWeight: 600 }}
              />
            )}
          </Box>
        )}

        {/* Row 4: result count */}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: hasActiveFilters ? 1.25 : 2 }}>
          Showing{" "}
          <strong>{displayedRows.length}</strong>
          {hasActiveFilters && displayedRows.length !== members.length ? (
            <> of <strong>{members.length}</strong> loaded</>
          ) : (
            <> of <strong>{totalRecords || members.length}</strong></>
          )}{" "}
          members
        </Typography>
      </Paper>

      {/* Data panel */}
      <Paper elevation={0} sx={{ ...sectionCardSx, minWidth: 0, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 280 }}>
            <CircularProgress />
          </Box>
        ) : displayedRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <GroupIcon sx={{ fontSize: 42, color: "#9aa6b2", mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? "#dde5f0" : "#223142" }}>
              No members found
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? "#8a9ab8" : "#6b778c", mt: 0.75 }}>
              {hasActiveFilters
                ? "No members match the current filters. Try adjusting or clearing them."
                : "Try updating your search to find matching club members."}
            </Typography>
            {hasActiveFilters && (
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                sx={{ ...secondaryButtonSx, mt: 2 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        ) : (
          <>
            {viewMode === "cards" ? (
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 1.25,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                  }}
                >
                  {cardsRows.map((m) => (
                    <MemberCard key={m.ClubMemberCode} m={m} />
                  ))}
                </Box>

                {displayedRows.length > cardsRows.length && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setCardVisibleCount((c) =>
                          Math.min(displayedRows.length, c + (isXs ? 12 : 24))
                        )
                      }
                      sx={{ ...secondaryButtonSx, fontWeight: 800 }}
                    >
                      Load more
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {isXs && (
                  <Typography
                    variant="caption"
                    sx={{ px: 2, pt: 1, color: "text.secondary", display: "block" }}
                  >
                    Swipe left/right to see more columns →
                  </Typography>
                )}

                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    display: "block",
                  }}
                >
                  <Box sx={{ minWidth: gridMinWidth, width: "100%" }}>
                    <DataGrid
                      rows={displayedRows}
                      columns={orderedColumns}
                      getRowId={(row) => row.ClubMemberCode}
                      autoHeight
                      disableRowSelectionOnClick
                      hideFooter
                      rowHeight={58}
                      columnVisibilityModel={columnVisibilityModel}
                      sortModel={sortModel}
                      onSortModelChange={(model) => setSortModel(model)}
                      sx={{
                        border: "none",
                        minWidth: gridMinWidth,
                        width: "100%",
                        backgroundColor: isDark ? "#16223a" : "transparent",
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(47,99,173,0.04)",
                          borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 800,
                          color: isDark ? "#dde5f0" : "#223142",
                        },
                        "& .MuiDataGrid-cell": {
                          borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(47,99,173,0.05)",
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: isDark ? "#dde5f0" : "#223142",
                        },
                        "& .MuiDataGrid-columnSeparator": { display: "none" },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(47,99,173,0.03)",
                        },
                        "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": { px: 2 },
                        "& .MuiDataGrid-footerContainer": {
                          borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : undefined,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                bgcolor: isDark ? "#16223a" : "#fff",
                minWidth: 0,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  disabled={loading || currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  sx={secondaryButtonSx}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  disabled={loading || currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  sx={secondaryButtonSx}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Column Order Dialog */}
      <Dialog open={colOrderOpen} onClose={() => setColOrderOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Arrange Columns
          <IconButton size="small" onClick={() => setColOrderOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
            Drag rows to reorder. Changes are saved to your account.
          </Typography>
          <List dense disablePadding>
            {(draftOrder || []).map((col, idx) => (
              <ListItem
                key={col.field}
                draggable
                onDragStart={() => { dragColRef.current = idx; }}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={() => {
                  const from = dragColRef.current;
                  const to = idx;
                  if (from === to) return;
                  setDraftOrder((prev) => {
                    const next = [...prev];
                    const [moved] = next.splice(from, 1);
                    next.splice(to, 0, moved);
                    return next;
                  });
                  dragColRef.current = null;
                }}
                sx={{
                  border: "1px solid rgba(47,99,173,0.1)",
                  borderRadius: "10px",
                  mb: 0.75,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" },
                  userSelect: "none",
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <DragIndicatorIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                </ListItemIcon>
                <ListItemText
                  primary={col.headerName}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem", color: isDark ? "#dde5f0" : "#223142" }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const reset = DEFAULT_COLUMN_ORDER;
              setColumnOrder(reset);
              saveColumnOrder(reset);
              setColOrderOpen(false);
            }}
            sx={{ textTransform: "none", borderRadius: "10px", borderColor: "rgba(47,99,173,0.3)", color: "#2f63ad" }}
          >
            Reset Default
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              const newOrder = (draftOrder || []).map((c) => c.field);
              setColumnOrder(newOrder);
              saveColumnOrder(newOrder);
              setColOrderOpen(false);
            }}
            sx={{ textTransform: "none", borderRadius: "10px", backgroundColor: "#2f63ad", fontWeight: 700 }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MembersGridPage;
